import { useId, useRef, useState } from "react";
import { Alert } from "@common/Alert";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { ReasonField } from "@admin/_components/ReasonField";
import { reasonProblem } from "@admin/_helpers/reason";
import { STATUS_META, type StatusAction } from "./status";

interface StatusTransitionDialogProps {
    action: StatusAction;
    orgName: string;
    isProcessing: boolean;
    // Inline failure copy from the parent's request. The dialog stays open on
    // failure so the admin can read it and retry.
    errorMessage: string | null;
    // False once the page no longer stands behind this action: another admin
    // moved the client (review finding 1), or it is gone. The confirm is
    // withdrawn and the reason locked, so the admin backs out and decides
    // again on the current status. Defaults to true.
    canConfirm?: boolean;
    // The reason trimmed, or undefined when none was given (AO-09: always
    // given when cancelling).
    onConfirm: (reason: string | undefined) => void;
    onClose: () => void;
}

// TODO: move onto @common/ConfirmDialog once PR 325 (which rewrites this dialog with a reason field) lands.
export const StatusTransitionDialog = ({
    action,
    orgName,
    isProcessing,
    errorMessage,
    canConfirm = true,
    onConfirm,
    onClose,
}: StatusTransitionDialogProps) => {
    // Local, so a failed request keeps what the admin typed for the retry,
    // and closing the dialog discards it.
    const [reason, setReason] = useState("");
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const reasonMessageId = useId();
    // closeOnBackdropClick false (review finding 2): a stray click outside the
    // panel must not throw away a typed reason, as in CancelOrderDialog.
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing,
        onRequestClose: onClose,
        closeOnBackdropClick: false,
    });

    const statusLabel = STATUS_META[action.target].label;
    const isReasonRequired = action.missingReasonMessage !== undefined;
    const trimmedReason = reason.trim();
    const problem = reasonProblem(reason, isReasonRequired);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                {...panelProps}
                className="w-full max-w-md rounded-lg bg-base-100 p-6 text-neutral shadow-xl focus:outline-none"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 id={titleId} className="mb-2 text-lg font-bold">
                    Set {orgName} to {statusLabel}?
                </h2>
                <p className="mb-4 text-sm">
                    This will set the client status to{" "}
                    <span className="font-semibold">{statusLabel}</span>.
                    {action.note && ` ${action.note}`}
                </p>

                <ReasonField
                    label={
                        isReasonRequired
                            ? "Reason (required)"
                            : "Reason (optional)"
                    }
                    value={reason}
                    onChange={setReason}
                    disabled={isProcessing || !canConfirm}
                    required={isReasonRequired}
                    hint="Saved to the client's status history."
                    missingMessage={action.missingReasonMessage}
                    messageId={reasonMessageId}
                />

                {errorMessage && (
                    <Alert color={ComponentColor.Error} className="mb-4">
                        {errorMessage}
                    </Alert>
                )}

                {/* The safe back-out comes first and is marked for the focus
                    trap, so a stray Enter never confirms (#452 / AO-09). */}
                <div className="flex justify-end gap-2">
                    <span data-dialog-initial-focus="" className="contents">
                        <Button
                            color={ComponentColor.Neutral}
                            size={ComponentSize.SM}
                            disabled={isProcessing}
                            onClick={onClose}
                        >
                            {action.dismissLabel ?? "Go back"}
                        </Button>
                    </span>
                    {canConfirm && (
                        <Button
                            color={action.color}
                            size={ComponentSize.SM}
                            loading={isProcessing}
                            disabled={problem !== null}
                            // A disabled confirm says why (review finding 8):
                            // the line under the reason names the problem.
                            aria-describedby={
                                problem !== null ? reasonMessageId : undefined
                            }
                            onClick={() =>
                                onConfirm(
                                    trimmedReason.length > 0
                                        ? trimmedReason
                                        : undefined,
                                )
                            }
                        >
                            {action.confirmLabel ?? action.label}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
